import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../ThemeContext';
import { useIsMobile, BottomSheet } from './LanguageSwitcher';

const THEME_META = {
    cyber:    { label: 'CYBER',    name: 'Cyberpunk', dot: 'bg-gradient-to-br from-cyber-purple to-cyber-cyan' },
    midnight: { label: 'MIDNIGHT', name: 'Midnight',  dot: 'bg-slate-700 border border-slate-500' },
    aurora:   { label: 'AURORA',   name: 'Aurora',    dot: 'bg-gradient-to-br from-purple-200 via-sky-200 to-rose-200 border border-slate-300' },
};

function ThemeDot({ theme }) {
    const meta = THEME_META[theme] || THEME_META.cyber;
    return (
        <span
            className={`inline-block rounded-sm border border-cyber-border/60 ${meta.dot}`}
            style={{ width: '1em', height: '1em' }}
            aria-hidden="true"
        />
    );
}

export default function ThemeSwitcher({
    placement = 'bottom',
    size = 'sm',
    className = '',
}) {
    const { t } = useTranslation();
    const { theme, setTheme, THEMES } = useTheme();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const isMobile = useIsMobile();

    useEffect(() => {
        if (!open) return;
        const onDocClick = (e) => {
            if (isMobile) return;
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocClick);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocClick);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, isMobile]);

    const menuPos = placement === 'top' ? 'bottom-full mb-1' : 'top-full mt-1';
    const triggerSize = size === 'md'
        ? 'text-xs px-3 py-2 gap-2'
        : 'text-[10px] px-2 py-1.5 gap-1.5';

    const currentMeta = THEME_META[theme] || THEME_META.cyber;

    return (
        <div ref={ref} className={`relative ${className}`}>
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className={`flex items-center font-display uppercase tracking-widest rounded-md border transition-all duration-200 ${triggerSize} ${
                    open
                        ? 'border-cyber-cyan/60 text-cyber-cyan bg-cyber-cyan/10'
                        : 'border-cyber-border text-cyber-dim hover:text-cyber-text'
                }`}
                aria-haspopup="listbox"
                aria-expanded={open}
            >
                <ThemeDot theme={theme} />
                <span>{currentMeta.label}</span>
                <span className={`text-[8px] transition-transform ${open ? 'rotate-180' : ''}`}>▼</span>
            </button>

            {open && !isMobile && (
                <ul
                    role="listbox"
                    className={`absolute right-0 ${menuPos} z-50 min-w-[150px] neon-panel py-1 shadow-neon-strong ${
                        placement === 'top' ? 'animate-popup-pop-up' : 'animate-popup-pop'
                    }`}
                >
                    {Object.keys(THEMES).map((key) => {
                        const meta = THEME_META[key] || { label: key.toUpperCase(), name: THEMES[key], dot: 'bg-cyber-border' };
                        const active = key === theme;
                        return (
                            <li key={key}>
                                <button
                                    type="button"
                                    onClick={() => { setTheme(key); setOpen(false); }}
                                    role="option"
                                    aria-selected={active}
                                    className={`w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-display tracking-widest text-left transition-colors ${
                                        active
                                            ? 'text-cyber-cyan bg-cyber-cyan/10'
                                            : 'text-cyber-dim hover:text-cyber-text hover:bg-cyber-border/30'
                                    }`}
                                >
                                    <ThemeDot theme={key} />
                                    <span className="flex-1">{meta.name}</span>
                                    {active && <span className="text-cyber-cyan">✓</span>}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {open && isMobile && createPortal(
                <BottomSheet
                    title={t('common.theme')}
                    onClose={() => setOpen(false)}
                    items={Object.keys(THEMES).map((key) => {
                        const meta = THEME_META[key] || { name: THEMES[key] };
                        return {
                            key,
                            active: key === theme,
                            onClick: () => { setTheme(key); setOpen(false); },
                            leading: <ThemeDot theme={key} />,
                            label: meta.name,
                        };
                    })}
                />,
                document.body
            )}
        </div>
    );
}
